<template>
  <div class="invoice-file-upload">
    <el-upload ref="uploader" drag :action="uploadUrl" :http-request="uploadFile" :show-file-list="false"
      accept=".pdf,.ofd,.jpg,.jpeg,.png" :disabled="busy || disabled" :before-upload="beforeUpload"
      :on-success="onSuccess" :on-error="onError" :on-progress="onProgress">
      <div v-if="busy" class="invoice-upload-content">
        <i class="el-icon-loading invoice-upload-icon"></i>
        <div>正在上传 {{ progress }}%</div>
      </div>
      <div v-else-if="value" class="invoice-upload-selected">
        <el-image v-if="isImage" :src="value" fit="contain" class="invoice-upload-image" />
        <i v-else class="el-icon-document invoice-upload-icon"></i>
        <div class="invoice-upload-filename" :title="selectedName">{{ selectedName }}</div>
        <div class="invoice-upload-replace">点击或拖拽文件重新上传</div>
      </div>
      <div v-else class="invoice-upload-content">
        <i class="el-icon-upload2 invoice-upload-icon"></i>
        <div class="invoice-upload-prompt"><span>点击上传</span> 或将文件拖到此处</div>
        <div class="invoice-upload-tip">支持 PDF、OFD、JPG、PNG，单个文件不超过 20MB</div>
      </div>
    </el-upload>
  </div>
</template>

<script>
require('@/utils/oss/hmac');
require('@/utils/oss/sha1');
import { env } from '@/utils/oss/ossConfig';
import Base64 from '@/utils/oss/Base64';
import Crypto from '@/utils/oss/crypto';
export default {
  name: 'InvoiceFileUpload',
  props: { value: { type: String, default: '' }, disabled: Boolean },
  data() {
    return { uploadUrl: env.uploadImageUrl, uploadData: {}, busy: false, progress: 0, uploadedName: '', pendingName: '' };
  },
  computed: {
    isImage() { return /\.(jpe?g|png)(?:[?#]|$)/i.test(this.value); },
    selectedName() {
      if (this.uploadedName) return this.uploadedName;
      try { return decodeURIComponent(this.value.split(/[?#]/)[0].split('/').pop()) || '已上传文件'; }
      catch (error) { return '已上传文件'; }
    },
  },
  watch: { value(value) { if (!value) this.uploadedName = ''; } },
  methods: {
    beforeUpload(file) {
      if (this.busy || this.disabled) return false;
      if (!/\.(pdf|ofd|jpe?g|png)$/i.test(file.name)) {
        this.$message.warning('仅支持 PDF、OFD、JPG、PNG 文件');
        return false;
      }
      if (file.size > 20 * 1024 * 1024) {
        this.$message.warning('单个文件不能超过 20MB');
        return false;
      }
      if (!file.size) { this.$message.warning('不能上传空文件'); return false; }
      const now = new Date();
      const expiration = new Date(now.getTime() + 30 * 60 * 1000).toISOString();
      const policy = Base64.encode(JSON.stringify({ expiration, conditions: [['content-length-range', 1, 20 * 1024 * 1024]] }));
      const signature = Crypto.util.bytesToBase64(Crypto.HMAC(Crypto.SHA1, policy, env.AccessKeySecret, { asBytes: true }));
      const suffix = file.name.split('.').pop().toLowerCase();
      const key = 'visa/' + now.getFullYear() + '/' + String(now.getMonth() + 1).padStart(2, '0') + '/' + String(now.getDate()).padStart(2, '0') + '/invoice-' + Date.now() + '-' + Math.random().toString(36).slice(2) + '.' + suffix;
      Object.assign(this.uploadData, { key, policy, signature, OSSAccessKeyId: env.OSSAccessKeyId, success_action_status: '200' });
      this.pendingName = file.name;
      this.progress = 0;
      this.busy = true;
      this.$emit('uploading', true);
      return this.$nextTick().then(() => file);
    },
    uploadFile(options) {
      const xhr = new XMLHttpRequest();
      const data = new FormData();
      const fields = this.uploadData;
      for (const name of ['key', 'policy', 'OSSAccessKeyId', 'signature', 'success_action_status']) {
        if (!fields[name]) {
          options.onError(new Error('缺少上传参数：' + name));
          return xhr;
        }
        data.append(name, fields[name]);
      }
      // OSS 要求文件字段放在所有签名参数之后。
      data.append('file', options.file, options.file.name);
      xhr.open('POST', this.uploadUrl, true);
      xhr.timeout = 120000;
      xhr.upload.onprogress = event => {
        if (event.lengthComputable) options.onProgress({ percent: event.loaded / event.total * 100 });
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) options.onSuccess(xhr.responseText);
        else options.onError(new Error(xhr.responseText || '上传失败：HTTP ' + xhr.status));
      };
      xhr.onerror = () => options.onError(new Error('上传网络异常'));
      xhr.ontimeout = () => options.onError(new Error('上传超时'));
      xhr.onabort = () => options.onError(new Error('上传已取消'));
      xhr.send(data);
      return xhr;
    },
    onProgress(event) { this.progress = Math.min(99, Math.round(event.percent || 0)); },
    onSuccess() {
      this.uploadedName = this.pendingName;
      const url = this.uploadUrl.replace(/\/$/, '') + '/' + this.uploadData.key;
      this.$emit('input', url);
      this.$emit('success', url);
      this.finishUpload();
    },
    onError(error) {
      const match = error && /<Code>([^<]+)<\/Code>/.exec(error.message || '');
      this.$message.error(match ? '上传失败：' + match[1] : '上传失败，请重试');
      this.finishUpload();
    },
    finishUpload() {
      this.busy = false;
      this.$emit('uploading', false);
      this.$refs.uploader.clearFiles();
    },
  },
};
</script>

<style scoped lang="scss">
.invoice-file-upload {
  width: 100%;
  ::v-deep .el-upload { display: block; width: 100%; line-height: normal; }
  ::v-deep .el-upload-dragger {
    width: 100%; height: 192px; padding: 0; box-sizing: border-box;
    border: 1px dashed #c0d4f5; border-radius: 4px; background: #f4f8ff;
    &:hover, &.is-dragover { border-color: #409eff; }
  }
  .invoice-upload-content, .invoice-upload-selected {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    width: 100%; height: 100%; padding: 20px; box-sizing: border-box;
  }
  .invoice-upload-icon { margin-bottom: 14px; font-size: 18px; color: #409eff; }
  .invoice-upload-prompt { font-size: 14px; line-height: 22px; color: #303133; span { color: #409eff; } }
  .invoice-upload-tip { margin-top: 20px; font-size: 12px; line-height: 20px; color: #909399; }
  .invoice-upload-image { width: 100%; height: 100px; flex-shrink: 0; }
  .invoice-upload-filename { max-width: 100%; margin-top: 8px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 13px; line-height: 20px; color: #303133; }
  .invoice-upload-replace { margin-top: 4px; font-size: 12px; line-height: 20px; color: #409eff; }
}
</style>
